import { z } from 'zod';

export const adminPageQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(40),
  cursor: z.string().max(4000).optional(),
});
const search = z.string().trim().max(200).optional();
const code = z
  .string()
  .trim()
  .max(100)
  .regex(/^[a-zA-Z0-9_.:-]+$/)
  .optional();
export const adminContentQuerySchema = adminPageQuerySchema
  .extend({
    q: search,
    userId: z.string().uuid().optional(),
    status: code,
    quality: z.enum(['complete', 'partial', 'link_only']).optional(),
    platform: code,
    deleted: z.enum(['active', 'deleted', 'all']).default('active'),
  })
  .strict();
export const adminJobsQuerySchema = adminPageQuerySchema
  .extend({
    q: search,
    userId: z.string().uuid().optional(),
    itemId: z.string().uuid().optional(),
    status: code,
    platform: code,
  })
  .strict();
export const adminAuditQuerySchema = adminPageQuerySchema
  .extend({
    actorId: z.string().uuid().optional(),
    targetUserId: z.string().uuid().optional(),
    targetType: code,
    action: code,
  })
  .strict();
export type AdminPage<T> = { items: T[]; nextCursor: string | null };
export type AdminAccountReference = { id: string; name: string; email: string };
export type AdminQuotaValues = {
  items: number;
  storageBytes: number;
  capturesPerDay: number;
  concurrentCaptures: number;
};
export type AdminUserSummary = AdminAccountReference & {
  role: 'admin' | 'user';
  verified: boolean;
  status: 'active' | 'disabled' | 'deleting';
  disabledAt: string | null;
  disabledReason: string | null;
  createdAt: string;
  adminRevision: number;
  lastActiveAt: string | null;
  activeSessionCount: number;
  usage: {
    itemCount: number;
    storageBytes: number;
    resourceBytes: number;
    documentBytes: number;
    uploadBytes: number;
    capturesToday: number;
    runningCaptures: number;
  };
  quota: {
    revision: number;
    overrides: Partial<AdminQuotaValues>;
    defaults: AdminQuotaValues;
    effective: AdminQuotaValues;
  };
};
export type AdminUserSession = {
  id: string;
  kind: string;
  name: string;
  createdAt: string;
  lastUsedAt: string;
  expiresAt: string;
  refreshExpiresAt: string | null;
};
export type AdminUserItemSummary = {
  id: string;
  title: string;
  url: string;
  platform: string;
  quality: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};
export type AdminItemSummary = AdminUserItemSummary & {
  userId: string;
  user: AdminAccountReference | null;
  sourceHost: string;
  kind: string;
};
export type AdminJobSummary = {
  id: string;
  userId: string;
  itemId: string;
  title: string | null;
  url: string | null;
  user: AdminAccountReference | null;
  status: string;
  attempts: number;
  errorCode: string | null;
  entrypoint: string;
  mode: string | null;
  platform: string | null;
  quality: string | null;
  engineVersion: string | null;
  rulesVersion: string | null;
  rulesHash: string | null;
  savedAssetCount: number | null;
  missingAssetCount: number | null;
  retryAt: string;
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string;
};
export type AdminCaptureAttempt = {
  id: string;
  status: string;
  errorCode: string | null;
  remoteStartedAt: string | null;
  remoteFinishedAt: string | null;
  remoteStatus: number | null;
  remoteErrorCode: string | null;
  engineVersion: string | null;
  rulesVersion: string | null;
  startedAt: string;
  finishedAt: string | null;
};
export type AdminJobDetail = { job: AdminJobSummary; attempts: AdminCaptureAttempt[] };
export type AdminAuditEntry = {
  id: string;
  source: 'admin' | 'runtime' | 'capture_rule_release';
  actorId: string | null;
  actor: AdminAccountReference | null;
  targetUserId: string | null;
  targetUser: AdminAccountReference | null;
  action: string;
  targetType: string;
  targetId: string | null;
  reason: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  createdAt: string;
};
export type AdminOverview = {
  version: string;
  buildSha: string;
  generatedAt: string;
  users: number;
  items: number;
  jobs: Array<{ status: string; _count: number }>;
  storageBytes: number;
  resourceBytes: number;
  documentBytes: number;
  uploadBytes: number;
  workerHeartbeat: string | null;
  cooldowns: Array<{ hostname: string; cooldownUntil: string; reason: string }>;
  pendingFeedback: number;
  recentChanges: AdminAuditEntry[];
};
